/**
 * Content Measurer
 *
 * Offscreen DOM measurement utility for auto-sizing nodes.
 * Measures intrinsic content size by rendering HTML into a hidden container
 * that inherits canvas CSS, then reading the resulting dimensions.
 *
 * Width-first measurement with typography heuristics:
 * 1. Let content determine natural width (up to maxWidth)
 * 2. Apply content-length heuristic to compute targetWidth (narrower for prose)
 * 3. Skip narrowing for code/table content (inherently landscape)
 * 4. Measure height at chosen width
 * 5. Optionally correct aspect ratio to prefer portrait
 * 6. Clamp to [minWidth, maxWidth]
 */

export interface ContentMeasurement {
	width: number; // world units (1:1 with CSS px at zoom=1)
	height: number;
}

export interface MeasureOptions {
	/** Max readable width before wrapping (default: 640) */
	maxWidth?: number;
	/** Minimum width (default: 100) */
	minWidth?: number;
	/** Minimum height (default: 40) */
	minHeight?: number;
	/** Padding to add around measured content (default: 0) */
	padding?: number;
	/** Character count — drives target width heuristic */
	contentLength?: number;
	/** Enable aspect ratio correction to prefer portrait (default: false) */
	preferPortrait?: boolean;
	/** Timeout for image loading in measureAsync (default: 5000ms) */
	imageLoadTimeout?: number;
	/**
	 * Fixed width for height-only measurement.
	 * When set, skip width heuristics and only measure height at this width.
	 */
	fixedWidth?: number;
}

const DEFAULT_OPTIONS: Required<MeasureOptions> = {
	maxWidth: 640,
	minWidth: 100,
	minHeight: 40,
	padding: 0,
	contentLength: 0,
	preferPortrait: false,
};

/**
 * Compute a target width based on content length for readable line lengths.
 * Returns 0 if no narrowing should be applied (short content or no length provided).
 */
export function computeTargetWidth(contentLength: number): number {
	if (contentLength < 80) return 0; // fit-content, no narrowing
	if (contentLength < 200) return 260;
	if (contentLength < 500) return 300;
	if (contentLength < 1000) return 340;
	return 380;
}

/** Returns true if the HTML contains code blocks, tables, or images that need wide layout. */
export function hasWideContent(html: string): boolean {
	return html.includes('<table') || html.includes('<pre') || html.includes('<img');
}

/** Returns true if the HTML contains image tags that load asynchronously. */
export function hasImageContent(html: string): boolean {
	return html.includes('<img');
}

/**
 * Measures HTML content size using an offscreen DOM element.
 *
 * The measurement container is a child of the canvas HTML layer container,
 * so it inherits all canvas CSS. The measurement element gets the `canvas-node`
 * class and a `data-type` attribute matching the node type, so type-specific
 * CSS applies.
 */
export class ContentMeasurer {
	private container: HTMLElement;
	private measureEl: HTMLElement;
	private pendingMeasure: Promise<void> = Promise.resolve();

	constructor(parentContainer: HTMLElement) {
		this.container = document.createElement('div');
		this.container.className = 'canvas-measure-offscreen';
		this.container.style.position = 'absolute';
		this.container.style.visibility = 'hidden';
		this.container.style.pointerEvents = 'none';
		this.container.style.zIndex = '-1';
		this.container.style.top = '-9999px';
		this.container.style.left = '-9999px';
		parentContainer.appendChild(this.container);

		this.measureEl = document.createElement('div');
		this.measureEl.className = 'canvas-node';
		this.container.appendChild(this.measureEl);
	}

	/**
	 * Measure the intrinsic size of HTML content for a given node type.
	 *
	 * @param html - The content HTML to measure
	 * @param nodeType - Node type (used for CSS class matching via data-type)
	 * @param options - Measurement constraints
	 * @returns Measured content size in world units
	 */
	measure(html: string, nodeType: string, options?: MeasureOptions): ContentMeasurement {
		const opts = { ...DEFAULT_OPTIONS, ...options };

		// Set up measurement element to match node rendering
		this.measureEl.dataset.type = nodeType;

		// Fixed-width mode: skip width heuristics, only measure height
		if (opts.fixedWidth != null && opts.fixedWidth > 0) {
			const width = opts.fixedWidth;
			this.measureEl.style.width = `${width}px`;
			this.measureEl.style.maxWidth = '';
			this.measureEl.style.height = 'auto';
			this.measureEl.style.overflow = 'visible';
			this.measureEl.innerHTML = html;

			let height = Math.ceil(this.measureEl.getBoundingClientRect().height);
			height = Math.max(opts.minHeight, height);
			height += opts.padding * 2;

			this.measureEl.innerHTML = '';
			return { width: width + opts.padding * 2, height };
		}

		// Phase 1: Measure natural width (max-content up to maxWidth)
		this.measureEl.style.width = 'max-content';
		this.measureEl.style.maxWidth = `${opts.maxWidth}px`;
		this.measureEl.style.height = 'auto';
		this.measureEl.style.overflow = 'visible';
		this.measureEl.innerHTML = html;

		let width = Math.ceil(this.measureEl.getBoundingClientRect().width);

		// Phase 2: Content-length heuristic — narrow prose content
		if (opts.contentLength > 0 && !hasWideContent(html)) {
			const target = computeTargetWidth(opts.contentLength);
			if (target > 0 && width > target) {
				width = Math.max(opts.minWidth, target);
			}
		}

		// Clamp width
		width = Math.max(opts.minWidth, Math.min(width, opts.maxWidth));

		// Phase 3: Measure height at chosen width
		this.measureEl.style.width = `${width}px`;
		this.measureEl.style.maxWidth = '';
		let height = Math.ceil(this.measureEl.getBoundingClientRect().height);

		// Phase 4: Aspect ratio correction — prefer portrait for prose
		if (opts.preferPortrait && width > height * 1.3 && width > opts.minWidth) {
			for (let i = 0; i < 3; i++) {
				const narrower = Math.max(opts.minWidth, Math.round(width * 0.8));
				if (narrower >= width) break; // no progress
				this.measureEl.style.width = `${narrower}px`;
				const newHeight = Math.ceil(this.measureEl.getBoundingClientRect().height);
				width = narrower;
				height = newHeight;
				if (width <= height * 1.3) break; // portrait enough
			}
		}

		// Clamp height
		height = Math.max(opts.minHeight, height);

		// Add padding
		width += opts.padding * 2;
		height += opts.padding * 2;

		// Clean up
		this.measureEl.innerHTML = '';

		return { width, height };
	}

	/**
	 * Measure the intrinsic size of HTML content, waiting for images to load.
	 *
	 * Same algorithm as measure(), but awaits image loading before reading
	 * dimensions. Serialized via promise chain to prevent clobbering the
	 * shared measureEl.
	 *
	 * Fast path: skips image waiting if the HTML has no <img> tags.
	 */
	measureAsync(html: string, nodeType: string, options?: MeasureOptions): Promise<ContentMeasurement> {
		const result = new Promise<ContentMeasurement>((resolve) => {
			this.pendingMeasure = this.pendingMeasure.then(async () => {
				const opts = { ...DEFAULT_OPTIONS, ...options };

				// Set up measurement element to match node rendering
				this.measureEl.dataset.type = nodeType;

				// Fixed-width mode: skip width heuristics, only measure height
				if (opts.fixedWidth != null && opts.fixedWidth > 0) {
					const width = opts.fixedWidth;
					this.measureEl.style.width = `${width}px`;
					this.measureEl.style.maxWidth = '';
					this.measureEl.style.height = 'auto';
					this.measureEl.style.overflow = 'visible';
					this.measureEl.innerHTML = html;

					if (hasImageContent(html)) {
						await this.waitForImages(this.measureEl, opts.imageLoadTimeout ?? 5000);
					}

					let height = Math.ceil(this.measureEl.getBoundingClientRect().height);
					height = Math.max(opts.minHeight, height);
					height += opts.padding * 2;

					this.measureEl.innerHTML = '';
					resolve({ width: width + opts.padding * 2, height });
					return;
				}

				// Phase 1: Measure natural width (max-content up to maxWidth)
				this.measureEl.style.width = 'max-content';
				this.measureEl.style.maxWidth = `${opts.maxWidth}px`;
				this.measureEl.style.height = 'auto';
				this.measureEl.style.overflow = 'visible';
				this.measureEl.innerHTML = html;

				// Wait for images to load (fast path: skip if no images)
				if (hasImageContent(html)) {
					await this.waitForImages(this.measureEl, opts.imageLoadTimeout ?? 5000);
				}

				let width = Math.ceil(this.measureEl.getBoundingClientRect().width);

				// Phase 2: Content-length heuristic — narrow prose content
				if (opts.contentLength > 0 && !hasWideContent(html)) {
					const target = computeTargetWidth(opts.contentLength);
					if (target > 0 && width > target) {
						width = Math.max(opts.minWidth, target);
					}
				}

				// Clamp width
				width = Math.max(opts.minWidth, Math.min(width, opts.maxWidth));

				// Phase 3: Measure height at chosen width
				this.measureEl.style.width = `${width}px`;
				this.measureEl.style.maxWidth = '';
				let height = Math.ceil(this.measureEl.getBoundingClientRect().height);

				// Phase 4: Aspect ratio correction — prefer portrait for prose
				if (opts.preferPortrait && width > height * 1.3 && width > opts.minWidth) {
					for (let i = 0; i < 3; i++) {
						const narrower = Math.max(opts.minWidth, Math.round(width * 0.8));
						if (narrower >= width) break;
						this.measureEl.style.width = `${narrower}px`;
						const newHeight = Math.ceil(this.measureEl.getBoundingClientRect().height);
						width = narrower;
						height = newHeight;
						if (width <= height * 1.3) break;
					}
				}

				// Clamp height
				height = Math.max(opts.minHeight, height);

				// Add padding
				width += opts.padding * 2;
				height += opts.padding * 2;

				// Clean up
				this.measureEl.innerHTML = '';

				resolve({ width, height });
			});
		});

		return result;
	}

	/**
	 * Wait for all images in an element to finish loading.
	 * Resolves when all images are loaded/errored, or after timeout.
	 */
	private waitForImages(el: HTMLElement, timeout: number): Promise<void> {
		const images = el.querySelectorAll('img');
		if (images.length === 0) return Promise.resolve();

		const imagePromises: Promise<void>[] = [];
		for (const img of images) {
			// Fast path: already loaded
			if (img.complete && img.naturalHeight > 0) continue;

			imagePromises.push(
				new Promise<void>((resolve) => {
					img.addEventListener('load', () => resolve(), { once: true });
					img.addEventListener('error', () => resolve(), { once: true });
				}),
			);
		}

		if (imagePromises.length === 0) return Promise.resolve();

		// Race against timeout
		return Promise.race([
			Promise.all(imagePromises).then(() => {}),
			new Promise<void>((resolve) => setTimeout(resolve, timeout)),
		]);
	}

	/** Remove the measurement container from the DOM. */
	destroy(): void {
		this.container.remove();
	}
}
